import React, { useEffect, useState } from "react";
import { LuImage as Image, LuVideo as Video, LuUpload as Upload, LuX as X } from "react-icons/lu";

export type InputFile = {
  id: string;
  name: string;
  size: number;
  type: "image" | "video";
  file: File;
  preview?: string;
};

type FileUploadInputProps = {
  handleInputFile: (files: InputFile[]) => void;
  reset?: boolean;
  maxFiles?: number;
};

export const FileUploadInput: React.FC<FileUploadInputProps> = ({ handleInputFile, reset, maxFiles = 10 }) => {
  const [mediaFiles, setMediaFiles] = useState<InputFile[]>([]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []) as File[];

    const newFiles: InputFile[] = files.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type.startsWith("image/") ? ("image" as const) : ("video" as const),
      file,
      preview: URL.createObjectURL(file),
    }));

    const updatedFiles = [...mediaFiles, ...newFiles].slice(0, maxFiles);
    setMediaFiles(updatedFiles);
    handleInputFile(updatedFiles);

    // Clear the input's value to allow re-upload of the same file
    event.target.value = "";
  };

  // Remove file
  const removeFile = (id: string) => {
    const updatedFiles = mediaFiles.filter((file) => file.id !== id);
    setMediaFiles(updatedFiles);
    handleInputFile(updatedFiles);
  };

  useEffect(() => {
    if (reset) setMediaFiles([]);
  }, [reset]);

  return (
    <div>
      {mediaFiles.length < maxFiles && (
        <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 hover:bg-gray-50 transition-all duration-300">
          <label className="block text-sm font-semibold text-gray-700">Media Upload</label>
          <input
            type="file"
            multiple={maxFiles > 1}
            accept="image/*,video/*"
            onChange={handleFileUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />

          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="p-4 bg-gray-100 rounded-full">
                <Upload className="w-8 h-8 text-gray-600" />
              </div>
            </div>

            <div>
              <p className="text-lg font-medium text-gray-900">Drop files here or click to upload</p>
              <p className="text-sm text-gray-500 mt-1">Support for images and videos up to 10MB each (max {maxFiles} files)</p>
            </div>

            <div className="flex justify-center space-x-6 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <Image className="w-4 h-4" />
                <span>Images</span>
              </div>
              <div className="flex items-center space-x-2">
                <Video className="w-4 h-4" />
                <span>Videos</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Uploaded Files Preview */}
      <div className={`space-y-4 ${mediaFiles.length >= maxFiles ? '' : 'mt-4'}`}>
        {/* Uploaded Files Preview */}
        {mediaFiles.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Uploaded Files ({mediaFiles.length}/{maxFiles})</h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {mediaFiles.map((file) => (
                <div
                  key={file.id}
                  className="relative bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <button
                    type="button"
                    onClick={() => removeFile(file.id)}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors z-10"
                  >
                    <X size={14} />
                  </button>

                  <div className="w-full h-32 bg-gradient-to-br from-blue-200 to-purple-200 flex items-center justify-center overflow-hidden">
                    {file.type === "image" ? (
                      <img src={file.preview} alt={file.name} className="object-cover w-full h-full" />
                    ) : (
                      <video src={file.preview} className="object-cover w-full h-full" controls />
                    )}
                  </div>

                  <div className="p-3 border-t bg-gray-50">
                    <p className="text-xs font-medium text-gray-800 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
